import React, { useState, useEffect, useRef, useContext } from "react";
import { Card, Row, Col, Spin, Alert, Switch, Badge, Button, Select } from "antd";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import { subscribeToRiderLocations } from "../utils/firebase";

const { Option } = Select;

const RiderTracking = () => {
  const [locations, setLocations] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStagnant, setShowStagnant] = useState(true);
  const [showRiders, setShowRiders] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [maxAgeMinutes, setMaxAgeMinutes] = useState(5); // Default 5 minutes
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const initialLoadRef = useRef(true); // Track if this is the initial load
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");

  // Helper function to convert bearing to compass direction
  const getCompassDirection = (bearing) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(bearing / 45) % 8;
    return directions[index];
  };

  // Helper function to get the correct bike icon based on bearing
  const getBikeIcon = (bearing) => {
    // Normalize bearing to 0-360
    const normalizedBearing = ((bearing % 360) + 360) % 360;

    // Determine direction based on bearing with 8-directional precision
    // We map 8 compass directions (N, NE, E, SE, S, SW, W, NW) to 4 bike icons
    // Each main direction covers 22.5 degrees on each side (45 degrees total)
    // Between main directions, we use the nearest icon for smooth transitions

    // N (337.5-22.5°): bike_up
    // NE (22.5-67.5°): bike_up (closer to north) or bike_right (closer to east)
    // E (67.5-112.5°): bike_right
    // SE (112.5-157.5°): bike_right (closer to east) or bike_down (closer to south)
    // S (157.5-202.5°): bike_down
    // SW (202.5-247.5°): bike_down (closer to south) or bike_left (closer to west)
    // W (247.5-292.5°): bike_left
    // NW (292.5-337.5°): bike_left (closer to west) or bike_up (closer to north)

    if (normalizedBearing >= 337.5 || normalizedBearing < 22.5) {
      // North
      return "/assets/bike_up.png";
    } else if (normalizedBearing >= 22.5 && normalizedBearing < 67.5) {
      // Northeast - use bike_right (more natural for diagonal movement)
      return "/assets/bike_right.png";
    } else if (normalizedBearing >= 67.5 && normalizedBearing < 112.5) {
      // East
      return "/assets/bike_right.png";
    } else if (normalizedBearing >= 112.5 && normalizedBearing < 157.5) {
      // Southeast - use bike_down (more natural for diagonal movement)
      return "/assets/bike_down.png";
    } else if (normalizedBearing >= 157.5 && normalizedBearing < 202.5) {
      // South
      return "/assets/bike_down.png";
    } else if (normalizedBearing >= 202.5 && normalizedBearing < 247.5) {
      // Southwest - use bike_left (more natural for diagonal movement)
      return "/assets/bike_left.png";
    } else if (normalizedBearing >= 247.5 && normalizedBearing < 292.5) {
      // West
      return "/assets/bike_left.png";
    } else {
      // Northwest (292.5-337.5°) - use bike_up (more natural for diagonal movement)
      return "/assets/bike_up.png";
    }
  };

  // Fetch location data from Directus
  const fetchLocationData = async () => {
    try {
      // Only show loading indicator on initial load
      if (locations.length === 0 && riders.length === 0) {
        setLoading(true);
      }

      // Fetch borrower locations from Directus - only those with valid coordinates
      const locationsResponse = await axios.get(
        `https://zedlenders.pickmesms.com/users`,
        {
          params: {
            fields: "id,first_name,last_name,phone,business,address,latitude,longitude,located_on,other_details",
            "filter[role][name][_eq]": "Borrower",
            "filter[company]": company?.id || 12,
            "filter[latitude][_nnull]": true,
            "filter[longitude][_nnull]": true,
            limit: -1,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (locationsResponse.data?.data) {
        const borrowers = locationsResponse.data.data;
        console.log("Fetched borrowers from Directus:", borrowers.length);
        console.log("First borrower sample:", borrowers[0]);

        // Filter out borrowers with invalid coordinates (0, 0 or null or empty strings)
        const validLocations = borrowers
          .filter(borrower => {
            console.log("Checking borrower:", {
              name: `${borrower.first_name} ${borrower.last_name}`,
              lat: borrower.latitude,
              lng: borrower.longitude,
              latType: typeof borrower.latitude,
              lngType: typeof borrower.longitude
            });

            // Check if latitude and longitude exist and are not empty
            if (!borrower.latitude || !borrower.longitude ||
                borrower.latitude === "" || borrower.longitude === "" ||
                borrower.latitude === "0" || borrower.longitude === "0") {
              console.log("Skipping - invalid coordinates");
              return false;
            }

            const lat = parseFloat(borrower.latitude);
            const lng = parseFloat(borrower.longitude);

            console.log("Parsed coordinates:", { lat, lng, isValidLat: !isNaN(lat), isValidLng: !isNaN(lng) });

            const isValid = lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng);
            console.log("Is valid:", isValid);

            return isValid;
          })
          .map(borrower => ({
            id: borrower.id,
            name: `${borrower.first_name || ""} ${borrower.last_name || ""}`.trim() || "Unknown",
            phone: borrower.phone || "N/A",
            business: borrower.business || "N/A",
            location: borrower.address || "No address",
            details: borrower.other_details || "No details",
            date: borrower.located_on || "N/A",
            latitude: parseFloat(borrower.latitude),
            longitude: parseFloat(borrower.longitude),
          }));

        console.log("Valid locations count:", validLocations.length);
        console.log("Valid locations:", validLocations);
        setLocations(validLocations);
      }

      // Riders are now fetched from Firebase in real-time via subscription
      // setRiders is called by the Firebase subscription callback
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error("Error fetching location data:", err);
      setError("Failed to fetch location data");
    } finally {
      setLoading(false);
    }
  };

  // Initialize Google Map (only once)
  const initMap = () => {
    if (!mapRef.current) {
      console.log("mapRef.current is null, cannot initialize");
      return;
    }

    console.log("Initializing map...", { mapRefExists: !!mapRef.current });

    try {
      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat: -15.3875, lng: 28.3228 }, // Lusaka coordinates
        zoom: 12,
        mapTypeId: "roadmap",
        mapId: "72967a99abd90a35fc4dcb3b", // Your Map ID for Advanced Markers
      });

      googleMapRef.current = map;
      console.log("Map initialized successfully", { map });

      // Force a resize to ensure map renders properly
      setTimeout(() => {
        window.google.maps.event.trigger(map, 'resize');
        console.log("Map resize triggered");

        // Set mapReady to true to trigger marker updates
        setMapReady(true);
        console.log("Map is now ready for markers");
      }, 300);

      // Add a listener to detect if map is being destroyed
      window.google.maps.event.addListener(map, 'tilesloaded', () => {
        console.log("Map tiles loaded");
      });
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  };

  // Clear existing markers
  const clearMarkers = () => {
    console.log("Clearing markers. Current count:", markersRef.current.length);
    markersRef.current.forEach((marker) => {
      // For AdvancedMarkerElement, set map to null to remove it
      if (marker) {
        marker.map = null;
      }
    });
    markersRef.current = [];
    console.log("Markers cleared");
  };

  // Update markers on map
  const updateMarkers = () => {
    console.log("updateMarkers called", {
      hasMap: !!googleMapRef.current,
      locationsLength: locations.length,
      ridersLength: riders.length
    });

    if (!googleMapRef.current) {
      console.warn("Map not initialized yet");
      return;
    }

    clearMarkers();

    // Add stagnant location markers (person icon for borrowers)
    if (showStagnant) {
      console.log("Adding borrower location markers. Total locations:", locations.length);

      locations.forEach((location, index) => {
        // Check for multiple possible field name variations
        const lat = location.latitude || location.lat;
        const lng = location.longitude || location.lng || location.lon;

        console.log(`Processing location ${index}:`, {
          name: location.name,
          lat,
          lng,
          latType: typeof lat,
          lngType: typeof lng,
          rawLocation: location
        });

        if (lat != null && lng != null && lat !== "" && lng !== "") {
          // Create a person icon marker element
          const markerElement = document.createElement("div");
          markerElement.innerHTML = `
            <div style="position: relative; display: inline-block;">
              <div style="
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background-color: #1890ff;
                display: flex;
                align-items: center;
                justify-content: center;
                border: 2px solid white;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
              ">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <div style="
                position: absolute;
                bottom: -20px;
                left: 50%;
                transform: translateX(-50%);
                background-color: white;
                padding: 2px 6px;
                border-radius: 4px;
                white-space: nowrap;
                font-size: 11px;
                font-weight: bold;
                color: #1890ff;
                box-shadow: 0 1px 3px rgba(0,0,0,0.2);
              ">${location.name || "Borrower"}</div>
            </div>
          `;

          try {
            const latValue = parseFloat(lat);
            const lngValue = parseFloat(lng);

            console.log(`Creating marker for location ${index} at:`, {
              lat: latValue,
              lng: lngValue,
              isValidLat: !isNaN(latValue),
              isValidLng: !isNaN(lngValue)
            });

            // Check if coordinates are valid numbers
            if (isNaN(latValue) || isNaN(lngValue)) {
              console.error(`Invalid coordinates for location ${index}:`, { lat, lng });
              return;
            }

            // Check if AdvancedMarkerElement is available
            let marker;
            if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
              marker = new window.google.maps.marker.AdvancedMarkerElement({
                position: { lat: latValue, lng: lngValue },
                map: googleMapRef.current,
                title: location.name || "Borrower Location",
                content: markerElement,
              });
              console.log(`AdvancedMarkerElement created for location ${index}`);
            } else {
              // Fallback to regular marker if AdvancedMarkerElement is not available
              console.warn("AdvancedMarkerElement not available, using regular Marker");
              marker = new window.google.maps.Marker({
                position: { lat: latValue, lng: lngValue },
                map: googleMapRef.current,
                title: location.name || "Borrower Location",
                icon: {
                  url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Ccircle cx='16' cy='16' r='14' fill='%231890ff' stroke='white' stroke-width='2'/%3E%3Cpath d='M16 16c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='white' transform='translate(0 -2)'/%3E%3C/svg%3E",
                  scaledSize: new window.google.maps.Size(32, 32),
                  anchor: new window.google.maps.Point(16, 16),
                },
              });
              console.log(`Regular Marker created for location ${index}`);
            }

            const infoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="padding: 8px; min-width: 200px;">
                  <h4 style="margin: 0 0 8px 0; color: #1890ff;">${location.name || "Borrower Location"}</h4>
                  <p style="margin: 4px 0;"><strong>Business:</strong> ${location.business || "N/A"}</p>
                  <p style="margin: 4px 0;"><strong>Phone:</strong> ${location.phone || "N/A"}</p>
                  <p style="margin: 4px 0;"><strong>Address:</strong> ${location.location || "N/A"}</p>
                  <p style="margin: 4px 0;"><strong>Details:</strong> ${location.details || "N/A"}</p>
                  <p style="margin: 4px 0;"><strong>Located on:</strong> ${location.date || "N/A"}</p>
                </div>
              `,
            });

            marker.addListener("click", () => {
              infoWindow.open(googleMapRef.current, marker);
            });

            markersRef.current.push(marker);
          } catch (error) {
            console.error(`Error creating marker for location ${index}:`, error);
          }
        }
      });
    }

    // Add rider markers (motorbike icon)
    if (showRiders) {
      console.log("Adding rider markers. Total riders:", riders.length);

      riders.forEach((rider, index) => {
        if (rider.latitude && rider.longitude) {
          try {
            const latValue = parseFloat(rider.latitude);
            const lngValue = parseFloat(rider.longitude);

            console.log(`Creating rider marker ${index} at:`, { lat: latValue, lng: lngValue });

            // Create a motorbike marker element for riders
            const bearing = rider.bearing || 0;
            const markerElement = document.createElement("img");
            markerElement.src = getBikeIcon(bearing); // Use directional icon based on bearing
            markerElement.style.width = "32px";
            markerElement.style.height = "32px";
            markerElement.style.cursor = "pointer";

            let marker;
            if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
              marker = new window.google.maps.marker.AdvancedMarkerElement({
                position: { lat: latValue, lng: lngValue },
                map: googleMapRef.current,
                title: rider.name,
                content: markerElement,
              });
              console.log(`AdvancedMarkerElement created for rider ${index}`);
            } else {
              console.warn("AdvancedMarkerElement not available, using regular Marker");
              marker = new window.google.maps.Marker({
                position: { lat: latValue, lng: lngValue },
                map: googleMapRef.current,
                title: rider.name,
                icon: {
                  url: getBikeIcon(bearing),
                  scaledSize: new window.google.maps.Size(32, 32),
                  anchor: new window.google.maps.Point(16, 16),
                },
              });
              console.log(`Regular Marker created for rider ${index}`);
            }

            const infoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="padding: 8px; min-width: 200px;">
                  <h4 style="margin: 0 0 8px 0; color: #ff4d4f;">🏍️ ${rider.name}</h4>
                  <p style="margin: 4px 0;"><strong>Status:</strong> <span style="color: #52c41a;">Active</span></p>
                  <p style="margin: 4px 0;"><strong>Going to:</strong> ${rider.debtorName}</p>
                  <p style="margin: 4px 0;"><strong>Location:</strong> ${rider.debtorLocation}</p>
                  <p style="margin: 4px 0;"><strong>Direction:</strong> ${bearing}° ${getCompassDirection(bearing)}</p>
                  <p style="margin: 4px 0;"><strong>Last Update:</strong> ${new Date(
                    rider.lastUpdate
                  ).toLocaleString()}</p>
                </div>
              `,
            });

            marker.addListener("click", () => {
              infoWindow.open(googleMapRef.current, marker);
            });

            markersRef.current.push(marker);
          } catch (error) {
            console.error(`Error creating rider marker ${index}:`, error);
          }
        }
      });
    }

    console.log("Markers update complete. Total markers added:", markersRef.current.length);

    // Only auto-fit bounds on initial load, not on subsequent updates
    // This prevents the map from zooming out when you've manually zoomed in
    if (initialLoadRef.current && markersRef.current.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      markersRef.current.forEach((marker) => {
        if (marker.position) {
          bounds.extend(marker.position);
        }
      });
      googleMapRef.current.fitBounds(bounds);

      // Add padding and adjust zoom if only one marker
      setTimeout(() => {
        if (markersRef.current.length === 1) {
          googleMapRef.current.setZoom(15);
        } else {
          const currentZoom = googleMapRef.current.getZoom();
          if (currentZoom > 15) {
            googleMapRef.current.setZoom(15);
          }
        }
        // Mark initial load as complete
        initialLoadRef.current = false;
      }, 100);
    }
  };

  useEffect(() => {
    console.log("Component mounted - fetching initial data");
    fetchLocationData();

    // Subscribe to real-time rider locations from Firebase
    console.log("Setting up Firebase rider location subscription with max age:", maxAgeMinutes, "minutes");
    const unsubscribe = subscribeToRiderLocations((firebaseRiders) => {
      console.log("Firebase riders updated:", firebaseRiders);
      setRiders(firebaseRiders);
      setLastUpdated(new Date());
    }, maxAgeMinutes);

    // Cleanup subscription on unmount
    return () => {
      console.log("Cleaning up Firebase subscription");
      unsubscribe();
    };
  }, [maxAgeMinutes]); // Re-subscribe when maxAgeMinutes changes

  // Initialize map only once
  useEffect(() => {
    const checkGoogleMaps = () => {
      console.log("Checking Google Maps availability...", {
        googleMaps: !!window.google?.maps,
        mapRef: !!mapRef.current,
        googleMapRef: !!googleMapRef.current
      });
      
      if (window.google?.maps && !googleMapRef.current && mapRef.current) {
        initMap();
      } else if (!window.google?.maps || !mapRef.current) {
        // Wait for Google Maps API and DOM element to be ready
        setTimeout(checkGoogleMaps, 100);
      }
    };
    
    // Small delay to ensure DOM is ready
    setTimeout(checkGoogleMaps, 100);
  }, []); // Empty dependency array = runs only once

  // Update markers when data changes OR when map becomes ready
  useEffect(() => {
    console.log("useEffect triggered - updating markers", {
      hasMap: !!googleMapRef.current,
      mapReady,
      locationsCount: locations.length,
      ridersCount: riders.length,
      showStagnant,
      showRiders
    });

    // Only update markers if map is ready AND we have data
    if (mapReady && googleMapRef.current && (locations.length > 0 || riders.length > 0)) {
      console.log("Conditions met - updating markers");
      // Small delay to ensure map is ready
      const timeoutId = setTimeout(() => {
        updateMarkers();
      }, 100);

      return () => clearTimeout(timeoutId);
    } else {
      console.log("Skipping marker update - conditions not met");
    }
  }, [locations, riders, showStagnant, showRiders, mapReady]);

  if (error) {
    return (
      <div className="container-fluid">
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Real-Time Rider Tracking Dashboard" loading={loading}>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: "center" }}>
                    <Badge
                      count={riders.length}
                      style={{ backgroundColor: "#ff4d4f" }}
                    >
                      <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                        Active Riders
                      </div>
                    </Badge>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: "center" }}>
                    <Badge
                      count={locations.length}
                      style={{ backgroundColor: "#1890ff" }}
                    >
                      <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                        Borrower Locations
                      </div>
                    </Badge>
                  </div>
                </Card>
              </Col>
              <Col span={4}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Switch
                    checked={showRiders}
                    onChange={setShowRiders}
                    style={{
                      backgroundColor: showRiders ? "#ff4d4f" : undefined,
                    }}
                  />
                  <span>Show Riders</span>
                </div>
              </Col>
              <Col span={4}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Switch
                    checked={showStagnant}
                    onChange={setShowStagnant}
                    style={{
                      backgroundColor: showStagnant ? "#1890ff" : undefined,
                    }}
                  />
                  <span>Show Borrowers</span>
                </div>
              </Col>
              <Col span={4}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span style={{ fontSize: "12px", color: "#666" }}>Filter Age:</span>
                  <Select
                    value={maxAgeMinutes}
                    onChange={setMaxAgeMinutes}
                    style={{ width: "100%" }}
                    size="small"
                  >
                    <Option value={2}>2 mins</Option>
                    <Option value={5}>5 mins</Option>
                    <Option value={10}>10 mins</Option>
                    <Option value={30}>30 mins</Option>
                    <Option value={60}>1 hour</Option>
                    <Option value={1440}>All (24h)</Option>
                  </Select>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Button
                  type="primary"
                  onClick={() => {
                    console.log("Manual refresh triggered");
                    console.log("Current state:", {
                      locations: locations.length,
                      riders: riders.length,
                      hasMap: !!googleMapRef.current,
                      markersCount: markersRef.current.length
                    });
                    fetchLocationData();
                  }}
                  style={{ marginRight: 8 }}
                >
                  Refresh Data
                </Button>
                <Button
                  onClick={() => {
                    console.log("Manual marker update triggered");
                    updateMarkers();
                  }}
                >
                  Update Markers
                </Button>
              </Col>
            </Row>

            <div style={{ position: "relative" }}>
              <div
                ref={mapRef}
                style={{
                  width: "100%",
                  height: "500px",
                  minHeight: "400px",
                  borderRadius: "8px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #d9d9d9",
                  position: "relative",
                }}
              />
              {loading && locations.length === 0 && riders.length === 0 && (
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "white",
                  borderRadius: "8px",
                }}>
                  <Spin size="large" />
                  <p>Loading map data...</p>
                </div>
              )}
            </div>

            <div
              style={{
                marginTop: 16,
                padding: 16,
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h4 style={{ margin: 0 }}>Map Legend</h4>
                {lastUpdated && (
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </div>
              <div
                style={{ display: "flex", gap: "24px", alignItems: "center", flexWrap: "wrap" }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div style={{ display: "flex", gap: "4px" }}>
                    <img
                      src="/assets/bike_up.png"
                      alt="Rider North"
                      style={{ width: 20, height: 20 }}
                    />
                    <img
                      src="/assets/bike_right.png"
                      alt="Rider East"
                      style={{ width: 20, height: 20 }}
                    />
                    <img
                      src="/assets/bike_down.png"
                      alt="Rider South"
                      style={{ width: 20, height: 20 }}
                    />
                    <img
                      src="/assets/bike_left.png"
                      alt="Rider West"
                      style={{ width: 20, height: 20 }}
                    />
                  </div>
                  <span>Active Riders (directional)</span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: "#1890ff",
                      border: "2px solid white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                  <span>Borrower Locations</span>
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RiderTracking;
